import React, { FormEvent, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userServices } from '../../services'
import { showBanner } from '../../global-state/banner-slice'
import { applyPostLoginNavigation } from '../../lib/utils/post-login'
import { PasswordStrengthIndicator } from '../signup-v2/PasswordStrengthIndicator'
import type { RootState } from '../../store'
import './promote-set-password.scss'

// Same charset as the login form, so the new password can always be typed back in.
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/

/**
 * Shown after the verify-email link or a login with the emailed temporary
 * password: the investor replaces it with their own, then continues onboarding.
 */
const SetOwnPasswordContainer = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { authToken, userId, email, name, acceptedNDA } = useSelector(
    (state: RootState) => state.investor,
  )
  const [currentPassword, setCurrentPassword] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!authToken) return <Navigate to='/login' replace />

  const continueToOnboarding = () =>
    applyPostLoginNavigation(
      dispatch,
      navigate,
      authToken,
      userId,
      email,
      name,
      acceptedNDA ?? false,
    )

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError('')

    if (!currentPassword) {
      setFormError('Please enter the temporary password from your email.')
      return
    }
    if (!passwordRegex.test(password)) {
      setFormError(
        'Password must be at least 8 characters with upper, lower, a digit and one of @ $ ! % * ? & (no other symbols).',
      )
      return
    }
    if (password !== passwordConfirmation) {
      setFormError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    const response = await userServices.setOwnPassword_v1(
      { currentPassword, password },
      { Authorization: authToken },
    )

    if (response?.status === 200) {
      dispatch(
        showBanner({ text: 'Password saved. Welcome to Credibila.', variant: 'success-banner' }),
      )
      await continueToOnboarding()
      return
    }
    if (response?.data?.code === 'password-change-not-required') {
      await continueToOnboarding()
      return
    }
    if (response?.data?.code === 'invalid-current-password') {
      setFormError('That temporary password is not correct. Copy it from the email we sent you.')
      setSubmitting(false)
      return
    }

    setFormError(
      (response?.data && response.data.message) || 'Something went wrong. Please try again.',
    )
    setSubmitting(false)
  }

  return (
    <div className='promote-set-password' data-testid='set-own-password-page'>
      <div className='promote-set-password__card'>
        <img className='promote-set-password__logo' src='/promote/logo-v2.png' alt='Credibila' />
        <h1 className='promote-set-password__title'>Choose your password</h1>
        <p className='promote-set-password__subtitle'>
          Choose your own password to continue. The temporary password we emailed you will stop
          working.
        </p>
        <form onSubmit={handleSubmit} noValidate data-testid='set-own-password-form'>
          <div className='promote-set-password__field'>
            <label htmlFor='own-current-password'>Temporary password</label>
            <input
              id='own-current-password'
              type='password'
              autoComplete='current-password'
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid='set-own-password-current'
            />
          </div>
          <div className='promote-set-password__field'>
            <label htmlFor='own-password'>New password</label>
            <input
              id='own-password'
              type='password'
              autoComplete='new-password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid='set-own-password-input'
            />
            <PasswordStrengthIndicator password={password} />
          </div>
          <div className='promote-set-password__field'>
            <label htmlFor='own-password-confirm'>Confirm password</label>
            <input
              id='own-password-confirm'
              type='password'
              autoComplete='new-password'
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              data-testid='set-own-password-confirm'
            />
          </div>
          {formError ? (
            <p className='promote-set-password__error' data-testid='set-own-password-error'>
              {formError}
            </p>
          ) : null}
          <button
            type='submit'
            className='promote-set-password__btn'
            disabled={submitting}
            data-testid='set-own-password-submit'
          >
            Save and continue
          </button>
        </form>
      </div>
    </div>
  )
}

export { SetOwnPasswordContainer }
